import $ from 'jquery';
import { Interval } from 'utilities/types';

/** Fixed labels per analysis detector (project_management#552) — never raw text into the DOM. */
const DETECTOR_LABELS: Record<string, string> = {
  outlier: 'outlier',
  rdropout: 'R-dropout',
};

/**
 * Noise (U) interval overlay. Human-annotated noise is grey and offers Delete.
 * Automatic noise (`isFromAnalysis` — since #552 the stretches the analysis
 * cut out before R-peak detection, tagged with `detector`) gets the `ecg`
 * modifier class (blue, see App.css), an "Auto · <detector>" label and a
 * tooltip explaining why there are no R-peaks inside.
 *
 * Automatic noise is never deleted (project_management#569): it offers
 * **Reject**, which records the reviewer's decision (`isValid: false`, kept by
 * reanalysis); a rejected one stays visible with the `rejected` class, a
 * "rejected" label and **Restore**. Dragging the bars to edit still works for
 * every kind (project_management#542).
 */
export const buildNoiseInterval = ({
  fromX,
  toX,
  content,
  annotationId,
  isFromAnalysis,
  detector,
  isValid,
  onClick,
  onReject,
  onRestore,
  chartHeight,
  chartWidth,
}: Interval) => {
  const height = chartHeight ?? 130;
  const top = 0;

  const maxRight = (chartWidth ?? 1200) - 100;
  const isPartialLeft = fromX < 0;
  const isPartialRight = toX > maxRight;

  const left = isPartialLeft ? 0 : fromX;
  const right = isPartialRight ? maxRight : toX;

  const width = right - left;

  const container = $('<div></div>').css({
    top,
    left,
    width,
    height,
    position: 'absolute',
  });

  const isRejected = Boolean(isFromAnalysis) && isValid === false;
  const detectorLabel = (detector && DETECTOR_LABELS[detector]) || '';
  const source = `${detectorLabel ? `Auto · ${detectorLabel}` : 'Auto'}${isRejected ? ' · rejected' : ''}`;
  const label = isFromAnalysis ? `<span class="source">${source}</span> ${content}` : content;
  let action = '<div class="delete">Delete</div>';

  if (isFromAnalysis) {
    action = isRejected ? '<div class="restore">Restore</div>' : '<div class="reject">Reject</div>';
  }

  let title = '';

  if (isRejected) {
    title = 'title="Rejected by a reviewer — kept as rejected after a reanalysis"';
  } else if (isFromAnalysis) {
    title = `title="Cut out by the analysis${detectorLabel ? ` (${detectorLabel})` : ''} — no R-peaks are detected in this stretch"`;
  }

  const ruler = $(`
    <div class="ruler-noise" data-id=${annotationId} ${title}>
        <div class="bar bar-left"></div>
        <div class="content">
            ${label}
            ${action}
        </div>
        <div class="bar bar-right"></div>
  </div>`);

  if (isFromAnalysis) {
    ruler.addClass('ecg');
    if (isRejected) ruler.addClass('rejected');
    $(ruler)
      .find(isRejected ? '.restore' : '.reject')
      .on('click', () => (isRejected ? onRestore : onReject)?.());
  } else {
    $(ruler).find('.content').on('click', onClick);
  }

  container.append(ruler);

  container.addClass('uplot-additional-element');

  return container;
};
